import { useState, useCallback } from 'react';

interface RequestCallback<TResult, Params extends any[]> {
  (...parameters: Params): Promise<TResult>;
}

interface UseRequestResult<TResult, Params extends any[]> {
  /**
   * Request wrapper
   */
  makeRequest: RequestCallback<TResult | undefined, Params>;
  /**
   * Indicates that request was ran
   */
  isLoading: boolean;
  /**
   * Request result
   */
  response: TResult | undefined;
  /**
   * Error which happened during request
   */
  error: any | null;
  /**
   * Allows to manually update response
   */
  setResponse: (value: TResult | undefined) => void;
}

/**
 * Request wrapper function which returns isLoading to determine should you show the data or loading indicator
 */
export function useRequest<TResult, Params extends any[]>(
  request: RequestCallback<TResult, Params>,
  // for some cases we need to show loading from initial state
  defaultLoadingState = false
): UseRequestResult<TResult, Params> {
  const [isLoading, setIsLoading] = useState(defaultLoadingState);
  const [response, setResponse] = useState<TResult | undefined>(undefined);
  const [error, setError] = useState<any | null>(null);

  const makeRequest: RequestCallback<TResult | undefined, Params> = useCallback(
    async (...parameters) => {
      setIsLoading(true);
      try {
        const data = await request(...parameters);
        setIsLoading(false);
        setResponse(data);
        return data;
      } catch (error) {
        setIsLoading(false);
        setError(error);
      }
    },
    [request]
  );

  return { makeRequest, isLoading, response, error, setResponse };
}
